import { initializeStore } from '@seamless/store';
import { computed, ref } from 'vue';
import { SMSPointOfService } from '@dh-io-emh/commerce-api/src/models/shared/ocb/api.schema';
import { infuse } from '../ioc-config';
import { IDealerContext } from '../models/dealer.model';
import { Logger } from '../utils/logger.util';
import {
  connectionName as dealerContextConnectionName,
  DealerContextState,
} from '../stores/dealerContext/dealerContext.connection';
import { ALL_OUTLET_IDS_PARAM, OUTLET_ID_PARAM } from '../context/dealer.context';

const dealerContext = ref<IDealerContext | null>(null);
const store = initializeStore();
const areDealerContextOutletsAvailable = ref<boolean>(false);

store.subscribe(dealerContextConnectionName, (contextState: DealerContextState) => {
  const { retailContext } = infuse('config');
  const _dealerContext = contextState.DealerContext;

  if (retailContext && Object.keys(_dealerContext ?? {}).length > 0) {
    dealerContext.value = _dealerContext;
  }
});

const allDealerOutlets = computed(() => {
  try {
    return dealerContext.value?.allOutletId ? dealerContext.value.allOutletId.split(',') : [];
  } catch (error) {
    Logger.error('DealerContext -> invalid "dealerContext.allOutletId" property');

    return [];
  }
});

const getDealerSpecificPointsOfServiceIds = (pointOfServices: SMSPointOfService[]): string[] => {
  const dealerIds = pointOfServices
    .filter(({ name }) => allDealerOutlets.value.includes(name as string))
    .map(({ name }) => name)
    .filter(Boolean) as string[];

  // returns all IDs from dealer context where the vehicle is available
  // if there are no matching IDs, just return all vehicle outlet IDs
  if (dealerIds.length) {
    areDealerContextOutletsAvailable.value = true;

    return dealerIds;
  }

  areDealerContextOutletsAvailable.value = false;

  return pointOfServices.map(({ name }) => name).filter(Boolean) as string[];
};

const addDealerContextParamsToUrl = (url: URL) => {
  if (!dealerContext.value) {
    return;
  }

  url.searchParams.set(OUTLET_ID_PARAM, dealerContext.value.outletId);
  url.searchParams.set(ALL_OUTLET_IDS_PARAM, dealerContext.value.allOutletId);
};

export const useDealerContext = () => ({
  dealerContext,
  allDealerOutlets,
  areDealerContextOutletsAvailable,
  getDealerSpecificPointsOfServiceIds,
  addDealerContextParamsToUrl,
});
